import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { getAssetByBarcode, createAsset, updateAsset, getAssetEvents, getAssets } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { Scan, Send, Package, Search, X, Camera, Keyboard, History, Settings, Users, MapPin, Tag, Wrench, ChevronRight } from 'lucide-react';
import { getStatusColor, getConditionColor, getStatusLabel, formatDateTime, getEventTypeLabel, canUserScan } from '../lib/utils';
import SendWorkflow from '../components/SendWorkflow';
import ReceiveWorkflow from '../components/ReceiveWorkflow';
import AssetHistory from '../components/AssetHistory';
import CameraScanner from '../components/CameraScanner';
import PhotoUpload from '../components/PhotoUpload';

// Helper function to check if user can access settings (Admin or Ops)
const canAccessSettings = (user) => {
  if (!user || !user.roles) return false;
  const roles = user.roles;
  return roles.includes('ADMIN') || roles.includes('OPS_COORDINATOR');
};

export default function Dashboard() {
  const { user, isAdminOrOps } = useAuth();
  const { selectedLocation, categories, getLocationName, getCategoryName } = useApp();
  const navigate = useNavigate();
  
  // Use both context function AND direct role check for maximum reliability
  const showSettingsWidget = canAccessSettings(user) || isAdminOrOps();
  
  const [mode, setMode] = useState(null); // 'scan', 'send', 'receive'
  const [scanInput, setScanInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [currentAsset, setCurrentAsset] = useState(null);
  const [scannedBarcode, setScannedBarcode] = useState('');
  const [useCameraScanner, setUseCameraScanner] = useState(false);
  
  // Add asset form - includes new fields: make, model, serial_number, purchase_cost, relationship_type
  const [newAsset, setNewAsset] = useState({
    barcode: '',
    category_id: '',
    year_bought: new Date().getFullYear(),
    purchase_date: '',
    purchase_cost: '',
    condition: 'WORKING',
    notes: '',
    make: '',
    model: '',
    serial_number: '',
    relationship_type: 'STANDALONE',
    parent_asset_id: '',
    photos: [],
  });

  // Parent assets for linking
  const [parentAssets, setParentAssets] = useState([]);

  const scanInputRef = useRef(null);

  // Auto-focus scan input when in scan mode
  useEffect(() => {
    if (mode === 'scan' && scanInputRef.current && !useCameraScanner) {
      scanInputRef.current.focus();
    }
  }, [mode, useCameraScanner]);

  const handleModeSelect = (newMode) => {
    if (newMode === 'scan' && !canUserScan(user)) {
      toast.error('Operations Coordinators cannot scan assets');
      return;
    }
    setMode(mode === newMode ? null : newMode);
    setScanInput('');
    setUseCameraScanner(false);
  };

  const handleBarcodeScan = useCallback(async (barcode) => {
    if (!barcode || !selectedLocation) return;
    
    const cleanBarcode = barcode.trim().toUpperCase();
    setScanInput('');
    
    try {
      const res = await getAssetByBarcode(cleanBarcode);
      
      // Asset exists
      setCurrentAsset(res.data);
      if (mode === 'scan') {
        setShowViewModal(true);
      } else if (mode === 'send') {
        if (res.data.asset_status === 'TRASHED') {
          toast.error('Cannot send trashed asset');
          return;
        }
        setShowSendModal(true);
      } else if (mode === 'receive') {
        if (!['IN_TRANSFER', 'IN_REPAIR'].includes(res.data.asset_status)) {
          toast.error('Asset is not in transfer or repair');
          return;
        }
        setShowReceiveModal(true);
      }
    } catch (error) {
      // Asset doesn't exist (404) or other error
      if (error.response?.status === 404) {
        if (mode === 'scan') {
          setScannedBarcode(cleanBarcode);
          setNewAsset(prev => ({ 
            ...prev, 
            barcode: cleanBarcode,
            relationship_type: 'STANDALONE',
            parent_asset_id: ''
          }));
          // Load parent assets at current location for potential linking
          try {
            const res = await getAssets({ 
              location_id: selectedLocation,
              relationship_type: 'PARENT'
            });
            // Filter to only show PARENT type assets
            setParentAssets(res.data.filter(a => a.relationship_type === 'PARENT'));
          } catch {
            setParentAssets([]);
          }
          setShowAddModal(true);
        } else {
          toast.error('Asset not found');
        }
      } else {
        toast.error('Error scanning barcode');
      }
    }
  }, [mode, selectedLocation]);

  const handleScanKeyDown = (e) => {
    if (e.key === 'Enter' && scanInput) {
      handleBarcodeScan(scanInput);
    }
  };

  const handleCameraScan = (barcode) => {
    setUseCameraScanner(false);
    handleBarcodeScan(barcode);
  };

  const handleAddAsset = async () => {
    if (!newAsset.category_id) {
      toast.error('Please select a category');
      return;
    }
    if (newAsset.year_bought < 1900 || newAsset.year_bought > 2100) {
      toast.error('Please enter a valid year');
      return;
    }
    if (newAsset.relationship_type === 'CHILD' && !newAsset.parent_asset_id) {
      toast.error('Please select a parent asset');
      return;
    }
    
    try {
      // Clean up the data - remove empty strings and convert purchase_cost
      const assetData = {
        ...newAsset,
        current_location_id: selectedLocation,
        purchase_cost: newAsset.purchase_cost ? parseFloat(newAsset.purchase_cost) : null,
        make: newAsset.make || null,
        model: newAsset.model || null,
        serial_number: newAsset.serial_number || null,
        purchase_date: newAsset.purchase_date || null,
        relationship_type: newAsset.relationship_type || 'STANDALONE',
        parent_asset_id: newAsset.relationship_type === 'CHILD' ? newAsset.parent_asset_id : null,
        photos: newAsset.photos || [],
      };
      
      const res = await createAsset(assetData);
      toast.success('Asset added successfully!');
      setShowAddModal(false);
      setNewAsset({
        barcode: '',
        category_id: '',
        year_bought: new Date().getFullYear(),
        purchase_date: '',
        purchase_cost: '',
        condition: 'WORKING',
        notes: '',
        make: '',
        model: '',
        serial_number: '',
        relationship_type: 'STANDALONE',
        parent_asset_id: '',
        photos: [],
      });
      // Auto-return to scan mode
      if (scanInputRef.current) {
        scanInputRef.current.focus();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add asset');
    }
  };

  const handleUpdateAsset = async () => {
    if (!currentAsset) return;
    
    try {
      await updateAsset(currentAsset.id, {
        condition: currentAsset.condition,
        notes: currentAsset.notes,
      });
      toast.success('Asset updated!');
      setShowViewModal(false);
      if (scanInputRef.current) {
        scanInputRef.current.focus();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update asset');
    }
  };

  const handleSearch = (e) => {
    if (e.key === 'Enter' && searchQuery) {
      handleBarcodeScan(searchQuery);
      setSearchQuery('');
    }
  };

  return (
    <div className="space-y-6">
      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
        <Input
          placeholder="Search by barcode..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={handleSearch}
          className="pl-10 h-12 text-lg"
          data-testid="search-input"
        />
      </div>

      {/* Admin/Ops Settings Button - Always visible for authorized users */}
      {showSettingsWidget && (
        <button
          onClick={() => navigate('/settings')}
          className="w-full bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white rounded-xl shadow-lg transition-all active:scale-[0.98] p-4"
          data-testid="settings-widget"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-white/20 rounded-lg">
                <Settings className="h-7 w-7" />
              </div>
              <div className="text-left">
                <h3 className="font-bold text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Settings & Management
                </h3>
                <p className="text-purple-100 text-sm">
                  Users • Locations • Categories • Repair Shops
                </p>
              </div>
            </div>
            <ChevronRight className="h-6 w-6 text-purple-200" />
          </div>
        </button>
      )}

      {/* Action Buttons */}
      <div className="grid grid-cols-3 gap-4">
        <button
          onClick={() => handleModeSelect('scan')}
          disabled={!canUserScan(user)}
          className={`h-32 flex flex-col items-center justify-center rounded-2xl shadow-lg transition-all active:scale-95 ${
            mode === 'scan' 
              ? 'bg-emerald-600 ring-4 ring-emerald-300 scan-active' 
              : 'bg-emerald-500 hover:bg-emerald-600'
          } text-white disabled:opacity-50 disabled:cursor-not-allowed`}
          data-testid="scan-btn"
        >
          <Scan className="h-10 w-10 mb-2" />
          <span className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>SCAN</span>
        </button>

        <button
          onClick={() => handleModeSelect('send')}
          className={`h-32 flex flex-col items-center justify-center rounded-2xl shadow-lg transition-all active:scale-95 ${
            mode === 'send' 
              ? 'bg-amber-600 ring-4 ring-amber-300' 
              : 'bg-amber-500 hover:bg-amber-600'
          } text-white`}
          data-testid="send-btn"
        >
          <Send className="h-10 w-10 mb-2" />
          <span className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>SEND</span>
        </button>

        <button
          onClick={() => handleModeSelect('receive')}
          className={`h-32 flex flex-col items-center justify-center rounded-2xl shadow-lg transition-all active:scale-95 ${
            mode === 'receive' 
              ? 'bg-blue-600 ring-4 ring-blue-300' 
              : 'bg-blue-500 hover:bg-blue-600'
          } text-white`}
          data-testid="receive-btn"
        >
          <Package className="h-10 w-10 mb-2" />
          <span className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>RECEIVE</span>
        </button>
      </div>

      {/* Active Mode Panel */}
      {mode && (
        <div className="bg-white rounded-xl shadow-md p-6 border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold capitalize" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {mode === 'scan' ? 'Scan Mode' : mode === 'send' ? 'Send Mode' : 'Receive Mode'}
            </h3>
            <Button variant="ghost" size="icon" onClick={() => setMode(null)} data-testid="close-mode-btn">
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Scanner Toggle */}
          <div className="flex gap-2 mb-4">
            <Button
              variant={!useCameraScanner ? 'default' : 'outline'}
              size="sm"
              onClick={() => setUseCameraScanner(false)}
              className={!useCameraScanner ? 'bg-purple-600 hover:bg-purple-700' : ''}
              data-testid="keyboard-scanner-btn"
            >
              <Keyboard className="h-4 w-4 mr-2" />
              Keyboard
            </Button>
            <Button
              variant={useCameraScanner ? 'default' : 'outline'}
              size="sm"
              onClick={() => setUseCameraScanner(true)}
              className={useCameraScanner ? 'bg-purple-600 hover:bg-purple-700' : ''}
              data-testid="camera-scanner-btn"
            >
              <Camera className="h-4 w-4 mr-2" />
              Camera
            </Button>
          </div>

          {/* Scanner Input */}
          {useCameraScanner ? (
            <CameraScanner onScan={handleCameraScan} />
          ) : (
            <div>
              <Label htmlFor="scanInput" className="text-sm text-gray-600 mb-2 block">
                Scan barcode or type manually
              </Label>
              <Input
                ref={scanInputRef}
                id="scanInput"
                value={scanInput}
                onChange={(e) => setScanInput(e.target.value)}
                onKeyDown={handleScanKeyDown}
                placeholder="Scan or enter barcode..."
                className="h-14 text-xl font-mono tracking-wider"
                autoFocus
                data-testid="scan-input"
              />
              <p className="text-xs text-gray-500 mt-2">
                Press Enter after scanning or typing the barcode
              </p>
            </div>
          )}
        </div>
      )}

      {/* Instruction Card */}
      {!mode && (
        <div className="bg-purple-50 rounded-xl p-6 border border-purple-100">
          <h3 className="font-semibold text-purple-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Quick Start Guide
          </h3>
          <ul className="text-sm text-purple-800 space-y-2">
            <li><strong>SCAN:</strong> Add new assets or view/update existing ones</li>
            <li><strong>SEND:</strong> Transfer assets to repair, other locations, or trash</li>
            <li><strong>RECEIVE:</strong> Receive incoming transfers or repaired assets</li>
          </ul>
        </div>
      )}

      {/* Add Asset Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Add New Asset</DialogTitle>
            <DialogDescription>
              Barcode: <span className="font-mono font-bold">{scannedBarcode}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Equipment Type *</Label>
              <Select value={newAsset.category_id} onValueChange={(v) => setNewAsset(prev => ({ ...prev, category_id: v }))}>
                <SelectTrigger data-testid="category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Make, Model, Serial Number - New Fields */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Make / Brand</Label>
                <Input
                  value={newAsset.make}
                  onChange={(e) => setNewAsset(prev => ({ ...prev, make: e.target.value }))}
                  placeholder="e.g., Dell, HP, Apple"
                  data-testid="make-input"
                />
              </div>
              <div>
                <Label>Model</Label>
                <Input
                  value={newAsset.model}
                  onChange={(e) => setNewAsset(prev => ({ ...prev, model: e.target.value }))}
                  placeholder="e.g., XPS 15, MacBook Pro"
                  data-testid="model-input"
                />
              </div>
            </div>
            
            <div>
              <Label>Serial Number</Label>
              <Input
                value={newAsset.serial_number}
                onChange={(e) => setNewAsset(prev => ({ ...prev, serial_number: e.target.value }))}
                placeholder="Enter serial number"
                className="font-mono"
                data-testid="serial-number-input"
              />
            </div>
            
            {/* Purchase Info */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Year Bought *</Label>
                <Input
                  type="number"
                  min="1900"
                  max="2100"
                  value={newAsset.year_bought}
                  onChange={(e) => setNewAsset(prev => ({ ...prev, year_bought: parseInt(e.target.value) || 0 }))}
                  data-testid="year-input"
                />
              </div>
              <div>
                <Label>Purchase Date</Label>
                <Input
                  type="date"
                  value={newAsset.purchase_date}
                  onChange={(e) => setNewAsset(prev => ({ ...prev, purchase_date: e.target.value }))}
                  data-testid="purchase-date-input"
                />
              </div>
            </div>
            
            <div>
              <Label>Purchase Cost ($)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={newAsset.purchase_cost}
                onChange={(e) => setNewAsset(prev => ({ ...prev, purchase_cost: e.target.value }))}
                placeholder="0.00"
                data-testid="purchase-cost-input"
              />
            </div>
            
            <div>
              <Label className="mb-2 block">Condition *</Label>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant={newAsset.condition === 'WORKING' ? 'default' : 'outline'}
                  className={`flex-1 h-12 ${newAsset.condition === 'WORKING' ? 'bg-green-600 hover:bg-green-700' : ''}`}
                  onClick={() => setNewAsset(prev => ({ ...prev, condition: 'WORKING' }))}
                  data-testid="condition-working-btn"
                >
                  WORKING
                </Button>
                <Button
                  type="button"
                  variant={newAsset.condition === 'BROKEN' ? 'default' : 'outline'}
                  className={`flex-1 h-12 ${newAsset.condition === 'BROKEN' ? 'bg-red-600 hover:bg-red-700' : ''}`}
                  onClick={() => setNewAsset(prev => ({ ...prev, condition: 'BROKEN' }))}
                  data-testid="condition-broken-btn"
                >
                  BROKEN
                </Button>
              </div>
            </div>
            
            <div>
              <Label>Notes</Label>
              <Textarea
                value={newAsset.notes}
                onChange={(e) => setNewAsset(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Optional notes about the asset..."
                data-testid="notes-input"
              />
            </div>
            
            {/* Asset Relationship Section */}
            <div className="border-t pt-4 mt-4">
              <Label className="mb-2 block">Asset Relationship</Label>
              <p className="text-xs text-gray-500 mb-3">Define how this asset relates to other assets.</p>
              <Select 
                value={newAsset.relationship_type} 
                onValueChange={(v) => setNewAsset(prev => ({ 
                  ...prev, 
                  relationship_type: v,
                  parent_asset_id: v !== 'CHILD' ? '' : prev.parent_asset_id
                }))}
              >
                <SelectTrigger data-testid="relationship-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="STANDALONE">Standalone (Not linked)</SelectItem>
                  <SelectItem value="PARENT">Parent (Can have children)</SelectItem>
                  <SelectItem value="CHILD">Child (Linked to parent)</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Parent asset selector - only if CHILD */}
              {newAsset.relationship_type === 'CHILD' && (
                <div className="mt-3">
                  <Label>Select Parent Asset *</Label>
                  {parentAssets.length > 0 ? (
                    <Select 
                      value={newAsset.parent_asset_id} 
                      onValueChange={(v) => setNewAsset(prev => ({ ...prev, parent_asset_id: v }))}
                    >
                      <SelectTrigger data-testid="parent-asset-select">
                        <SelectValue placeholder="Select parent asset" />
                      </SelectTrigger>
                      <SelectContent>
                        {parentAssets.map((asset) => (
                          <SelectItem key={asset.id} value={asset.id}>
                            {asset.barcode} ({getCategoryName(asset.category_id)})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-sm text-amber-600 mt-1">No parent assets available at this location. Create a parent asset first.</p>
                  )}
                </div>
              )}
            </div>

            {/* Photo Upload Section */}
            <div className="border-t pt-4">
              <PhotoUpload
                photos={newAsset.photos}
                onPhotosChange={(photos) => setNewAsset(prev => ({ ...prev, photos }))}
                required={false}
                entityType="asset"
                entityId="new"
                label="Initial Photos (Optional)"
                helpText="Document the asset condition when adding to inventory"
              />
            </div>
            
            <Button 
              onClick={handleAddAsset} 
              className="w-full bg-purple-600 hover:bg-purple-700"
              data-testid="add-asset-submit-btn"
            >
              Add Asset
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Asset Modal */}
      <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Asset Details</DialogTitle>
            <DialogDescription className="sr-only">View and update asset information</DialogDescription>
          </DialogHeader>
          {currentAsset && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">Barcode</p>
                <p className="font-mono text-2xl font-bold tracking-wider">{currentAsset.barcode}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500">Category</p>
                  <p className="font-medium">{getCategoryName(currentAsset.category_id)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Year Bought</p>
                  <p className="font-medium">{currentAsset.year_bought}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Status</p>
                  <Badge className={getStatusColor(currentAsset.asset_status)}>
                    {getStatusLabel(currentAsset.asset_status)}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Location</p>
                  <p className="font-medium">{getLocationName(currentAsset.current_location_id)}</p>
                </div>
              </div>

              <div>
                <Label className="mb-2 block">Condition</Label>
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant={currentAsset.condition === 'WORKING' ? 'default' : 'outline'}
                    className={`flex-1 ${currentAsset.condition === 'WORKING' ? 'bg-green-600 hover:bg-green-700' : ''}`}
                    onClick={() => setCurrentAsset(prev => ({ ...prev, condition: 'WORKING' }))}
                    disabled={currentAsset.asset_status === 'TRASHED'}
                    data-testid="view-condition-working-btn"
                  >
                    WORKING
                  </Button>
                  <Button
                    type="button"
                    variant={currentAsset.condition === 'BROKEN' ? 'default' : 'outline'}
                    className={`flex-1 ${currentAsset.condition === 'BROKEN' ? 'bg-red-600 hover:bg-red-700' : ''}`}
                    onClick={() => setCurrentAsset(prev => ({ ...prev, condition: 'BROKEN' }))}
                    disabled={currentAsset.asset_status === 'TRASHED'}
                    data-testid="view-condition-broken-btn"
                  >
                    BROKEN
                  </Button>
                </div>
              </div>

              <div>
                <Label>Notes</Label>
                <Textarea
                  value={currentAsset.notes || ''}
                  onChange={(e) => setCurrentAsset(prev => ({ ...prev, notes: e.target.value }))}
                  disabled={currentAsset.asset_status === 'TRASHED'}
                  data-testid="view-notes-input"
                />
              </div>

              <div className="flex gap-2">
                <Button 
                  onClick={handleUpdateAsset} 
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                  disabled={currentAsset.asset_status === 'TRASHED'}
                  data-testid="update-asset-btn"
                >
                  Update
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowViewModal(false);
                    setShowHistoryModal(true);
                  }}
                  data-testid="view-history-btn"
                >
                  <History className="h-4 w-4 mr-2" />
                  History
                </Button>
              </div>

              {currentAsset.asset_status !== 'TRASHED' && (
                <div className="flex gap-2 pt-2 border-t">
                  <Button
                    variant="outline"
                    className="flex-1 border-amber-500 text-amber-600 hover:bg-amber-50"
                    onClick={() => {
                      setShowViewModal(false);
                      setShowSendModal(true);
                    }}
                    data-testid="quick-send-btn"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    Send
                  </Button>
                  {['IN_TRANSFER', 'IN_REPAIR'].includes(currentAsset.asset_status) && (
                    <Button
                      variant="outline"
                      className="flex-1 border-blue-500 text-blue-600 hover:bg-blue-50"
                      onClick={() => {
                        setShowViewModal(false);
                        setShowReceiveModal(true);
                      }}
                      data-testid="quick-receive-btn"
                    >
                      <Package className="h-4 w-4 mr-2" />
                      Receive
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Send Workflow Modal */}
      <SendWorkflow
        open={showSendModal}
        onClose={() => {
          setShowSendModal(false);
          setCurrentAsset(null);
          if (scanInputRef.current) scanInputRef.current.focus();
        }}
        asset={currentAsset}
      />

      {/* Receive Workflow Modal */}
      <ReceiveWorkflow
        open={showReceiveModal}
        onClose={() => {
          setShowReceiveModal(false);
          setCurrentAsset(null);
          if (scanInputRef.current) scanInputRef.current.focus();
        }}
        asset={currentAsset}
      />

      {/* Asset History Modal */}
      <AssetHistory
        open={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        asset={currentAsset}
      />
    </div>
  );
}
